// Building classification search (pure), as in the design: all typed words must match the
// Georgian or English name; names starting with the text first, then a word starting with it,
// shorter names first. Results are grouped: a match whose ancestor also matches sits only under
// it; a group lists up to 8 sub-types (when more, 6 with matching ones first, then "… N more").
// Item: { code, level, parent, name_ka, name_en }

const MAX_ROWS = 14;
const MAX_KIDS_SHOWN = 8;
const KIDS_WHEN_CUT = 6;

export function indexTypes(items) {
  const byCode = {};
  const kidsOf = {};
  items.forEach((x) => {
    byCode[x.code] = x;
    (kidsOf[x.parent || ""] = kidsOf[x.parent || ""] || []).push(x);
  });
  return { items, byCode, kidsOf };
}

/** Root → … → the type itself. */
export function typePath(index, code) {
  const out = [];
  let x = index.byCode[code];
  while (x) {
    out.unshift(x);
    x = index.byCode[x.parent];
  }
  return out;
}

export function childrenOf(index, code) {
  return index.kidsOf[code] || [];
}

/** Rows to show: { type, depth (0 group, 1 sub-type), kids (count) } or { more, type (group) }. */
export function searchTypes(index, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return [];
  const words = q.split(/\s+/).filter(Boolean);
  const rank = (x) => {
    const ka = x.name_ka.toLowerCase();
    const en = (x.name_en || "").toLowerCase();
    if (ka.startsWith(q) || en.startsWith(q)) return 0;
    return ` ${ka} ${en}`.replace(/[^\s\wა-ჿ]/g, " ").includes(` ${words[0]}`) ? 1 : 2;
  };
  const hits = index.items
    .filter((x) => words.every((w) => `${x.name_ka} ${x.name_en || ""}`.toLowerCase().includes(w)))
    .sort((a, b) => rank(a) - rank(b) || a.name_ka.length - b.name_ka.length);
  const hitSet = new Set(hits.map((x) => x.code));
  const underHit = (x) => {
    let p = index.byCode[x.parent];
    while (p) {
      if (hitSet.has(p.code)) return true;
      p = index.byCode[p.parent];
    }
    return false;
  };
  const rows = [];
  hits
    .filter((x) => !underHit(x))
    .forEach((root) => {
      if (rows.length >= MAX_ROWS) return;
      const kids = childrenOf(index, root.code);
      rows.push({ type: root, depth: 0, kids: kids.length });
      const limit = kids.length > MAX_KIDS_SHOWN ? KIDS_WHEN_CUT : kids.length;
      const ordered =
        limit < kids.length ? [...kids.filter((k) => hitSet.has(k.code)), ...kids.filter((k) => !hitSet.has(k.code))] : kids;
      ordered.slice(0, limit).forEach((k) => rows.push({ type: k, depth: 1, kids: childrenOf(index, k.code).length }));
      if (kids.length > limit) rows.push({ more: kids.length - limit, type: root, depth: 1 });
    });
  return rows;
}

/** Codes starting 01.01 use the residential template; no other templates exist yet. */
export const hasOwnTemplate = (code) => String(code || "").startsWith("01.01");
